import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { useEntryEvents } from '#pplx/frontend/apps/ask/analytics/useEntryEvents.js';
import { useThreadUserMentionsVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useThreadUserMentionsVariation.js';
import { getThreadMembersQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { mentionThreadMembers } from '#pplx/frontend/apps/ask/queries/thread-queries/mentionThreadMembers.js';
import { getSubmittedMentionedUsers } from '#pplx/frontend/apps/ask/user-mentions/internal/getSubmittedMentionedUsers.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
const messages = defineMessages({
    singleMentionTitle: {
        defaultMessage: 'Share this thread with {mention}?',
        id: '724b350487',
        description: 'Question asking whether to share a thread with one mentioned user',
    },
    multipleMentionsTitle: {
        defaultMessage: 'Share this thread with {count, plural, one {# mentioned member} other {# mentioned members}}?',
        id: 'afac317fe5',
        description: 'Question asking whether to share a thread with multiple mentioned users',
    },
    description: {
        defaultMessage: 'They may not have access yet. Sharing will give them access and notify them.',
        id: '362747e86f',
        description: 'Explanation in the user mention thread-access prompt',
    },
    shareLabel: {
        defaultMessage: 'Share',
        id: '1ee4d4cd1c',
        description: 'Button that shares a thread with mentioned users',
    },
    sharedSuccess: {
        defaultMessage: 'Shared with {count, plural, one {# member} other {# members}}',
        id: 'd8eb1db777',
        description: 'Toast confirming the thread was shared with mentioned users',
    },
    shareError: {
        defaultMessage: "Couldn't share with the mentioned members. Try again.",
        id: '78bccb8638',
        description: 'Error toast when sharing a thread with mentioned users fails',
    },
});
const USER_MENTION_ACCESS_TOAST_DEDUP_KEY = 'user-mention-access';
const isThreadPathname = (pathname, threadSlug) => {
    const encodedThreadSlug = encodeURIComponent(threadSlug);
    return (pathname.endsWith(`/search/${encodedThreadSlug}`) ||
        pathname.endsWith(`/computer/tasks/${encodedThreadSlug}`));
};
const isAccessToastPathname = (pathname, toast) => pathname === toast.originPathname || isThreadPathname(pathname, toast.threadSlug);
export const useUserMentionAccessToast = () => {
    const { variation: isEnabled } = useThreadUserMentionsVariation({ defaultValue: false });
    const { trackEvent } = useEntryEvents();
    const { session } = useSession();
    const { openToast } = useToastContext();
    const pathname = usePathname();
    const activeAccessToastRef = useRef(null);
    const queryClient = useQueryClient();
    const { $t } = useIntl();
    const currentUserEmail = session?.user?.email?.toLowerCase();
    const closeActiveAccessToast = useCallback(() => {
        const toast = activeAccessToastRef.current;
        if (!toast) {
            return;
        }
        toast.handle.closeToast();
        activeAccessToastRef.current = null;
    }, []);
    useEffect(() => {
        const toast = activeAccessToastRef.current;
        if (!toast || isAccessToastPathname(pathname, toast)) {
            return;
        }
        closeActiveAccessToast();
    }, [closeActiveAccessToast, pathname]);
    useEffect(() => () => closeActiveAccessToast(), [closeActiveAccessToast]);
    const { mutate: mentionThreadMembersMutation } = useMutation({
        mutationFn: ({ entryUUID, mentionedUsers }) => mentionThreadMembers({
            params: {
                backend_uuid: entryUUID,
                mentioned_org_user_uuids: mentionedUsers.map((user) => user.orgUserUUID),
            },
            reason: 'user-mention-share-confirmed',
        }),
        onSuccess: (response, { entryUUID }) => {
            void queryClient.invalidateQueries({ queryKey: getThreadMembersQueryKey(entryUUID) });
            const sharedCount = response.results.filter((result) => result.shared).length;
            if (sharedCount === 0) {
                return;
            }
            openToast({
                message: $t(messages.sharedSuccess, { count: sharedCount }),
                variant: 'success',
                timeoutSeconds: 5,
                dedupKey: `user-mention-toast:${entryUUID}`,
            });
        },
        onError: (error, { entryUUID }) => {
            logger.error('Failed to share thread with mentioned users', {
                error,
                backendUuid: entryUUID,
            });
            openToast({
                message: $t(messages.shareError),
                variant: 'error',
                timeoutSeconds: 5,
                dedupKey: `user-mention-error-toast:${entryUUID}`,
            });
        },
    });
    return useCallback(({ entryUUID, mentions, threadSlug }) => {
        if (!isEnabled || !entryUUID)
            return;
        const mentionedUsers = getSubmittedMentionedUsers(mentions, currentUserEmail);
        const firstMentionedUser = mentionedUsers[0];
        if (!firstMentionedUser) {
            return;
        }
        const mentionCount = mentionedUsers.length;
        const handle = openToast({
            message: mentionCount === 1
                ? $t(messages.singleMentionTitle, { mention: firstMentionedUser.displayLabel })
                : $t(messages.multipleMentionsTitle, { count: mentionCount }),
            description: $t(messages.description),
            variant: 'neutral',
            timeoutSeconds: null,
            iconOverride: $icon('user'),
            ctaText: $t(messages.shareLabel),
            ctaOnClick: () => {
                trackEvent('thread mention access selected', {
                    action: 'share',
                    entryUUID,
                    mentionCount,
                });
                mentionThreadMembersMutation({ entryUUID, mentionedUsers });
            },
            onClose: ({ reason }) => {
                if (reason === 'close-button') {
                    trackEvent('thread mention access selected', {
                        action: 'do_nothing',
                        entryUUID,
                        mentionCount,
                    });
                }
            },
            dedupKey: USER_MENTION_ACCESS_TOAST_DEDUP_KEY,
        });
        activeAccessToastRef.current = {
            handle,
            originPathname: pathname,
            threadSlug,
        };
    }, [
        currentUserEmail,
        isEnabled,
        mentionThreadMembersMutation,
        openToast,
        pathname,
        trackEvent,
        $t,
    ]);
};
//# sourceMappingURL=useUserMentionAccessToast.js.map